import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Link2, Flag, Loader2 } from "lucide-react";
import { computeWbsIds } from "@/lib/wbsIds";

const DEP_TYPES = [
  { value: "FS", label: "FS — Finish to Start" },
  { value: "SS", label: "SS — Start to Start" },
  { value: "FF", label: "FF — Finish to Finish" },
  { value: "SF", label: "SF — Start to Finish" },
];

const depId = (d) => (typeof d === "string" ? d : d.predecessor_id);

export default function LinkTasksDialog({ open, onOpenChange, tasks, phases, initialTaskId, onSave }) {
  const [taskId, setTaskId] = useState("");
  const [deps, setDeps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("wbs");

  useEffect(() => {
    if (open) { setTaskId(initialTaskId || tasks[0]?.id || ""); setFilter(""); }
  }, [open, initialTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = tasks.find((x) => x.id === taskId);
    setDeps((t?.dependencies || []).map((d) =>
      typeof d === "string" ? { predecessor_id: d, type: "FS", lag: 0 } : { type: "FS", lag: 0, ...d }));
  }, [taskId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const task = tasks.find((x) => x.id === taskId);
  const dependsOn = (t, targetId, seen = new Set()) => {
    if (seen.has(t.id)) return false;
    seen.add(t.id);
    return (t.dependencies || []).some((d) => {
      const pid = depId(d);
      if (pid === targetId) return true;
      const pred = tasks.find((x) => x.id === pid);
      return pred ? dependsOn(pred, targetId, seen) : false;
    });
  };
  const candidates = tasks.filter((x) => x.id !== taskId && !dependsOn(x, taskId));
  const depFor = (id) => deps.find((d) => depId(d) === id);
  const wbsIds = useMemo(() => computeWbsIds(tasks || [], phases || []), [tasks, phases]);
  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matches = (t) => !q || t.title.toLowerCase().includes(q) || (wbsIds[t.id] || "").toLowerCase().startsWith(q);
    if (sort === "az") {
      const items = candidates.filter(matches).sort((a, b) => a.title.localeCompare(b.title));
      return items.length ? [{ label: null, items }] : [];
    }
    const segs = (t) => { const id = wbsIds[t.id] || ""; return id.startsWith("M") ? [1e9, Number(id.slice(1))] : id.split(".").map(Number); };
    const cmp = (a, b) => { const A = segs(a), B = segs(b); for (let i = 0; i < Math.max(A.length, B.length); i++) { const d = (A[i] ?? 0) - (B[i] ?? 0); if (d) return d; } return 0; };
    const gs = (phases || []).map((g, i) => ({ label: `${i + 1} · ${g}`, items: candidates.filter((t) => t.phase === g && matches(t)).sort(cmp) }));
    const known = new Set(phases || []);
    const other = candidates.filter((t) => !known.has(t.phase) && matches(t)).sort(cmp);
    if (other.length) gs.push({ label: "No phase", items: other });
    return gs.filter((g) => g.items.length);
  }, [candidates, filter, sort, wbsIds, phases]);
  const matchCount = groups.reduce((n, g) => n + g.items.length, 0);
  const depth = (t) => { const id = wbsIds[t.id] || ""; return id.startsWith("M") ? 0 : Math.max(0, id.split(".").length - 2); };
  const successors = useMemo(() => (taskId ? tasks.filter((t) => t.id !== taskId && (t.dependencies || []).some((d) => depId(d) === taskId)) : []), [tasks, taskId]);

  const toggle = (id) => setDeps((prev) =>
    prev.some((d) => depId(d) === id) ? prev.filter((d) => depId(d) !== id) : [...prev, { predecessor_id: id, type: "FS", lag: 0 }]);
  const update = (id, patch) => setDeps((prev) => prev.map((d) => (depId(d) === id ? { ...d, ...patch } : d)));

  const save = async () => {
    if (!task) return;
    setSaving(true);
    try {
      await onSave(task, deps);
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="link-tasks-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Link tasks & milestones</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Task or milestone</Label>
            <Select value={taskId} onValueChange={setTaskId}>
              <SelectTrigger className="max-w-full [&>span]:min-w-0 [&>span]:truncate" data-testid="link-task-select"><SelectValue placeholder="Pick a task" /></SelectTrigger>
              <SelectContent className="max-w-[440px]">
                {tasks.map((t) => (
                  <SelectItem key={t.id} value={t.id}><span className="block max-w-[380px] truncate"><span className="font-mono text-[11px] font-bold text-muted-foreground mr-1.5">{wbsIds[t.id] || ""}</span>{t.is_milestone ? "▼ " : ""}{t.title}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Depends on (predecessors)</Label>
            <p className="text-xs text-muted-foreground -mt-0.5">Check every task or milestone that must happen before this one. Dates auto-shift to respect the links.</p>
            <div className="flex items-center gap-2">
              <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by task ID (e.g. 2.3) or name…" className="h-8 text-sm flex-1" data-testid="link-dep-search-input" />
              <button type="button" onClick={() => setSort((s) => (s === "wbs" ? "az" : "wbs"))}
                className="shrink-0 text-[11px] font-bold px-2.5 py-1.5 rounded-md border border-border hover:bg-secondary text-muted-foreground"
                title="Toggle sort order" data-testid="link-dep-sort-toggle">
                {sort === "wbs" ? "WBS order" : "A–Z"}
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto border border-border rounded-md" data-testid="link-predecessor-list">
              {groups.map((g, gi) => (
                <div key={g.label || "az"}>
                  {g.label && (
                    <div className={`sticky top-0 z-10 px-2 py-1 bg-secondary/80 backdrop-blur-sm border-b border-border ${gi > 0 ? "border-t" : ""}`} data-testid="link-dep-group-header">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{g.label}</span>
                    </div>
                  )}
                  <div className="p-2 space-y-1">
                    {g.items.map((c) => {
                      const dep = depFor(c.id);
                      return (
                        <div key={c.id} className={`rounded-md px-1.5 py-1 ${dep ? "bg-primary/5" : "hover:bg-secondary/50"}`} style={{ marginLeft: depth(c) * 16 }}>
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={!!dep} onCheckedChange={() => toggle(c.id)} data-testid={`link-pred-${c.id}`} />
                            <span className="font-mono text-[11px] font-bold text-muted-foreground w-12 shrink-0 text-right">{wbsIds[c.id] || "—"}</span>
                            {c.is_milestone && <Flag className="h-3 w-3 text-primary shrink-0" />}
                            <span className="truncate flex-1">{c.title}</span>
                            {c.end_date && <span className="text-[10px] text-muted-foreground shrink-0">{c.end_date}</span>}
                          </label>
                          {dep && (
                            <div className="flex items-center gap-2 mt-1 ml-6">
                              <Select value={dep.type || "FS"} onValueChange={(v) => update(c.id, { type: v })}>
                                <SelectTrigger className="h-7 w-40 text-xs" data-testid={`link-type-${c.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>{DEP_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                              </Select>
                              <Input type="number" value={dep.lag ?? 0} onChange={(e) => update(c.id, { lag: Number(e.target.value) })} className="h-7 w-16 text-xs" data-testid={`link-lag-${c.id}`} />
                              <span className="text-[11px] text-muted-foreground">days lag</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {candidates.length === 0 && <p className="text-xs text-muted-foreground p-2">No other tasks to link yet.</p>}
              {candidates.length > 0 && matchCount === 0 && <p className="text-xs text-muted-foreground p-2">No tasks match "{filter}".</p>}
            </div>
            {successors.length > 0 && (
              <div className="pt-1">
                <Label className="text-xs">Successors — depend on this task</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="link-successors">
                  {successors.map((s) => {
                    const d = (s.dependencies || []).find((x) => depId(x) === taskId);
                    const typ = (typeof d === "object" && d?.type) || "FS";
                    return (
                      <span key={s.id} className="inline-flex items-center gap-1.5 text-xs bg-secondary rounded-md px-2 py-1" data-testid={`link-successor-${s.id}`}>
                        <span className="font-mono text-[10px] font-bold text-muted-foreground">{wbsIds[s.id] || "—"}</span>
                        <span className="max-w-[220px] truncate">{s.title}</span>
                        <span className="text-[10px] font-bold text-muted-foreground">{typ}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !task} className="gap-1.5" data-testid="link-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Save links
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
